/**
 * The actual device, as the app itself sees it (owner ask, 2026-09-29).
 *
 * The WebView user agent names the Android model but never the iPhone model,
 * so the admin Devices tab could not tell an iPhone 12 from a 16 Pro, and no
 * build ever said which app version a trainer was on. @capacitor/device and
 * @capacitor/app answer both. The report rides on the app heartbeat
 * (lib/appHeartbeat), which already fires on open and on every return to the
 * foreground and follows whoever is signed in, so no extra request exists.
 *
 * Native builds only: a browser has no model to report, and the server keeps
 * reading the user agent for those. Read once per launch; the model does not
 * change while the app is open.
 *
 * Contents: model identifier, maker, OS and WebView version, app version and
 * build, and whether it is a simulator. Nothing identifying the person: no
 * device name (which is often "Sam's iPhone"), no identifiers.
 *
 * PUSH PERMISSION (2026-10-15 owner ask: can a slipping regular be reached
 * at all). The OS's answer for this app, read with checkPermissions, which
 * never prompts. Unlike the model it can change while the app is open (the
 * trainer flips it in Settings), so it is read on every beat rather than
 * once per launch. "unsupported" is an honest "could not read it": an older
 * native shell, or the plugin throwing.
 */
import { Capacitor } from "@capacitor/core";

/** Mirrors PUSH_PERMISSIONS in cloudflare/src/lib/accountDevice.ts. */
export type PushPermission = "granted" | "denied" | "prompt" | "unsupported";

export interface DeviceReport {
  platform: "ios" | "android";
  model: string;
  manufacturer: string | null;
  osVersion: string | null;
  webViewVersion: string | null;
  appVersion: string | null;
  appBuild: string | null;
  isVirtual: boolean;
  pushPermission: PushPermission;
}

/** The half that cannot change while the app is open. */
type StaticReport = Omit<DeviceReport, "pushPermission">;

let cached: Promise<StaticReport | null> | null = null;

/** Capacitor's PermissionState, folded to the four words the server keeps. */
export function foldPushPermission(receive: unknown): PushPermission {
  switch (receive) {
    case "granted":
      return "granted";
    case "denied":
      return "denied";
    case "prompt":
    case "prompt-with-rationale":
      return "prompt";
    default:
      return "unsupported";
  }
}

async function readPushPermission(): Promise<PushPermission> {
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const res = await PushNotifications.checkPermissions();
    return foldPushPermission(res?.receive);
  } catch {
    // No plugin in this shell, or it threw: say so rather than guess.
    return "unsupported";
  }
}

async function read(): Promise<StaticReport | null> {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  if (platform !== "ios" && platform !== "android") return null;
  try {
    const { Device } = await import("@capacitor/device");
    const info = await Device.getInfo();
    let appVersion: string | null = null;
    let appBuild: string | null = null;
    try {
      const { App } = await import("@capacitor/app");
      const app = await App.getInfo();
      appVersion = app.version || null;
      appBuild = app.build || null;
    } catch {
      /* the device half is still worth sending */
    }
    if (!info.model) return null;
    return {
      platform,
      model: info.model,
      manufacturer: info.manufacturer || null,
      osVersion: info.osVersion || null,
      webViewVersion: info.webViewVersion || null,
      appVersion,
      appBuild,
      isVirtual: !!info.isVirtual,
    };
  } catch {
    // An older native shell without the plugin: say nothing, never throw.
    return null;
  }
}

/**
 * The report for this beat, or null off-native / on failure. Never rejects.
 * The device half is read once per launch; the push permission every time.
 */
export async function getDeviceReport(): Promise<DeviceReport | null> {
  if (!cached) cached = read().catch(() => null);
  const base = await cached;
  if (!base) return null;
  return { ...base, pushPermission: await readPushPermission() };
}

/** Test seam. */
export function _resetDeviceReportForTests(): void {
  cached = null;
}
